import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

/**
 * Store for Signals: the catalog of signal types, the member's saved signals, and their feed.
 * Talks to the internal /cm/signals API. Same shape as the other MobX stores: observable state plus
 * async actions on makeApi (which resolves to the axios error on failure, never throws).
 */
const POLL_MS = 30 * 1000;
// A first check (or "Check now") is over once its saved signal shows a check from then on, or after 3 minutes.
export const FIRST_CHECK_MS = 3 * 60 * 1000;
const SKEW_MS = 60 * 1000;

/**
 * The catalog's description can be {reads, gives, example}: keep it as `descriptionParts` and leave `description` a
 * string, so every place that prints it (agent pickers included) keeps working.
 */
export const withTextDescription = def => {
  const d = def && def.description;
  if (!d || typeof d !== "object") return def;
  return { ...def, descriptionParts: d, description: d.gives || d.reads || "" };
};

class SignalsData {
  @observable catalog = [];
  @observable saved = [];
  @observable savedLoading = false;
  // True when the last /saved load failed, so the page offers a retry instead of the first-visit layout.
  @observable savedError = false;
  @observable feedItems = [];
  // One account's or contact's own rows ({items, total, loading, error, loadedAt} by entity id), so an account page,
  // a contact card on top of it and the Signals feed never overwrite each other.
  @observable entityFeeds = {};
  @observable feedTotal = 0;
  @observable feedLoading = false;
  // True when the last feed load failed (the feed then offers a retry instead of "all caught up").
  @observable feedError = false;
  // The newest open entries from the last load or poll; the "N new" chips count from here so they move with the poll.
  @observable latestItems = [];
  @observable unreadCount = 0;
  // The few newest open signals, for the bell popover (kept apart from the feed list so opening the bell can't reshuffle it).
  @observable recentItems = [];
  @observable notificationSettings = null;
  // Feed entries that arrived after the feed was last loaded ("3 new since you opened").
  @observable newSinceOpen = 0;
  @observable feedLoadedAt = 0;
  // [{signalType, signalLabel, action, actions, matched, outOf, templateId, message}]
  @observable suggestions = [];
  // Competitors from the seller profile: {hasSellerProfile, sellerProfileId, competitors: [{name, domain, bucket, linkedinUrl, source}]}.
  @observable competitors = null;
  @observable competitorsLoading = false;
  // domain -> {loading, data: {posts, insights, generatedAt, reason?}}; kept for the session.
  @observable competitorInsights = {};
  // A hiring intel watch just started (or was checked now): {savedSignalId, startedAt, accounts}. The feed shows a
  // "reading hiring posts" row and reloads every 10 s until the check is over (see firstCheckDone).
  @observable firstCheck = null;
  // The seller profiles an account signal can be read for: {defaultId, profiles: [{id, name, website, isYourCompany}]}; null until loaded.
  @observable sellerProfiles = null;
  _sellerProfilesInflight = null;
  // "domain|persona" -> {loading, data: {people, reason}, promise}: who to reach at an account, kept for the session.
  @observable people = {};
  // sellerProfileId -> the last competitors view loaded for it, so switching back to a profile draws instantly.
  competitorsByProfile = {};
  _pollTimer = null;
  _feedLast = null; // {key, at}: the last finished load
  _feedInflight = null; // {key, promise}: the page and the feed ask for the same first page at mount; one request serves both

  fill(url, params = {}) {
    let filled = url;
    Object.keys(params).forEach(k => {
      filled = filled.replace(`<<${k}>>`, encodeURIComponent(params[k]));
    });
    return filled;
  }

  // 200 and 204 are both a success here (the feed's actions answer 204 No Content).
  ok(res) {
    return !!res && res.status >= 200 && res.status < 300;
  }

  // The server's reason ({error}) for a 4xx, else a generic line.
  failure(res, fallback) {
    const msg = (res && res.response && res.response.data && res.response.data.error) || fallback;
    toasterState.setToastMsg(msg, "failure");
    return msg;
  }

  @action.bound
  async fetchCatalog() {
    const res = await makeApi({ url: URLS.signalsCatalog, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.catalog = res.data.map(withTextDescription);
    return this.catalog;
  }

  @action.bound
  async fetchSaved() {
    this.savedLoading = true;
    // Turn on then Undo fires two of these; an older reply landing last would bring the removed agent back.
    const seq = (this._savedSeq = (this._savedSeq || 0) + 1);
    const res = await makeApi({ url: URLS.signalsSaved, method: "GET" });
    if (seq !== this._savedSeq) return this.saved;
    this.savedError = !(this.ok(res) && Array.isArray(res.data));
    if (!this.savedError) this.saved = res.data;
    this.savedLoading = false;
    return this.saved;
  }

  // Resolves to {signal} on success or {error} with the server's reason, so the drawer can show it inline.
  @action.bound
  // `records`: how many records the new watch covers (the first-check row says "at N accounts").
  async saveSignal(id, payload, { records } = {}) {
    const res = id
      ? await makeApi({ url: this.fill(URLS.signalsSavedOne, { id }), method: "PUT", data: payload })
      : await makeApi({ url: URLS.signalsSaved, method: "POST", data: payload });
    if (!this.ok(res)) {
      const error = (res && res.response && res.response.data && res.response.data.error) || "Could not save the signal.";
      return { error };
    }
    const savedId = res.data && res.data.signal && res.data.signal.id;
    if (!id && payload && payload.signalType === "hiring_intel" && savedId) this.startFirstCheck(savedId, records);
    await this.fetchSaved();
    return { signal: res.data };
  }

  /* ───────── custom signals ───────── */

  // The four starters; the page has its own copy until this loads.
  @observable customTemplates = [];

  @action.bound
  async fetchCustomTemplates() {
    const res = await makeApi({ url: URLS.signalsCustomTemplates, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data) && res.data.length) this.customTemplates = res.data;
    return this.customTemplates;
  }

  // Draft, preview, create and update all resolve {data} or {error} (the server's reason, else `fallback`), never toast:
  // the drawer says it inline next to what went wrong.
  async customCall(req, fallback) {
    const res = await makeApi(req);
    if (this.ok(res) && res.data) return { data: res.data };
    const status = res && res.response && res.response.status;
    return { error: (status && status < 500 && this.reasonOf(res)) || fallback, status };
  }

  draftCustom(text) {
    return this.customCall({ url: URLS.signalsCustomDraft, method: "POST", data: { text } }, "Couldn't read that right now. Try again.");
  }

  previewCustom(definition, target) {
    return this.customCall({ url: URLS.signalsCustomPreview, method: "POST", data: { definition, target, limit: 5 } }, "Couldn't run the preview right now. Try again.");
  }

  @action.bound
  async saveCustom(id, body) {
    const out = id
      ? await this.customCall({ url: this.fill(URLS.signalsCustomOne, { id }), method: "PUT", data: body }, "Couldn't save your changes. Try again.")
      : await this.customCall({ url: URLS.signalsCustom, method: "POST", data: { frequency: "weekly", ...body } }, "Couldn't start tracking. Try again.");
    if (out.data) await this.fetchSaved();
    return out;
  }

  @action.bound
  startFirstCheck(savedSignalId, accounts = null) {
    this.firstCheck = { savedSignalId, startedAt: Date.now(), accounts: accounts == null ? null : Number(accounts) };
  }

  @action.bound
  endFirstCheck() {
    this.firstCheck = null;
  }

  /** True once the running first check is over: its saved signal was stamped checked since it started, or it timed out. */
  firstCheckDone(now = Date.now()) {
    const fc = this.firstCheck;
    if (!fc || now - fc.startedAt > FIRST_CHECK_MS) return true;
    const row = this.saved.find(r => r.signal && r.signal.id === fc.savedSignalId);
    const at = row ? new Date(row.lastCheckedAt || row.signal.lastCheckedAt || 0).getTime() : 0;
    return at > 0 && at >= fc.startedAt - SKEW_MS;
  }

  /**
   * Up to 3 people with a persona at an account ({people: [{name, title, linkedinUrl, photoUrl, location, entityUrn,
   * alreadyContactId}], reason?}). One request per domain + persona for the session; a failed one asks again next time.
   */
  @action.bound
  fetchPeople({ domain, persona, accountId }) {
    const key = `${domain || accountId || ""}|${persona || ""}`.toLowerCase();
    const have = this.people[key];
    if (have && !have.failed) return have.promise || Promise.resolve(have.data);
    const params = { persona };
    if (domain) params.domain = domain;
    if (accountId) params.accountId = accountId;
    const promise = makeApi({ url: URLS.signalsPeople, method: "GET", params }).then(res => {
      const good = this.ok(res) && res.data;
      const data = good ? { people: (Array.isArray(res.data.people) ? res.data.people : []).filter(Boolean).slice(0, 3), reason: res.data.reason || null } : { people: [], reason: "unavailable" };
      this.setPeople(key, { loading: false, data, failed: data.reason === "unavailable" });
      return data;
    });
    this.setPeople(key, { loading: true, data: null, promise });
    return promise;
  }

  peopleFor({ domain, persona, accountId }) {
    return this.people[`${domain || accountId || ""}|${persona || ""}`.toLowerCase()] || null;
  }

  @action.bound
  setPeople(key, entry) {
    this.people = { ...this.people, [key]: entry };
  }

  /** A person from the card became a contact (a lookup or an add saved them): later opens show "Open contact". */
  @action.bound
  markPersonContact(query, linkedinUrl, contactId, email) {
    const key = `${query.domain || query.accountId || ""}|${query.persona || ""}`.toLowerCase();
    const have = this.people[key];
    if (!have || !have.data || !contactId) return;
    const people = have.data.people.map(p => (p.linkedinUrl === linkedinUrl ? { ...p, alreadyContactId: contactId, ...(email ? { email } : {}) } : p));
    this.setPeople(key, { ...have, data: { ...have.data, people } });
  }

  /** Runs a tracked signal's check now (owner only, once an hour). Toasts the outcome; resolves true when it started. */
  @action.bound
  async checkNow(id, signalType) {
    const res = await makeApi({ url: `${this.fill(URLS.signalsSavedOne, { id })}/check-now`, method: "POST" });
    if (!this.ok(res)) {
      const status = res && res.response && res.response.status;
      // The hourly limit is a rule, not an error: say it as a neutral note.
      if (status === 429) toasterState.setToastMsg((res.response.data && res.response.data.error) || "You can check this once an hour. Try again a bit later.", "info");
      else this.failure(res, "Couldn't start a check right now. Try again.");
      return false;
    }
    // A check already running: say so instead of "checking now" again.
    if (res.data && res.data.started === false && res.data.running) {
      toasterState.setToastMsg("Already checking. New ones show up in your feed when it finishes.", "info");
      return true;
    }
    if (signalType === "hiring_intel") this.startFirstCheck(id);
    toasterState.setToastMsg(signalType === "hiring_intel" ? "Checking now. Reading what your accounts are hiring for…" : "Checking now. New ones show up in your feed.", "success");
    return true;
  }

  @action.bound
  async setPaused(id, paused) {
    const res = await makeApi({ url: this.fill(paused ? URLS.signalsPause : URLS.signalsResume, { id }), method: "POST" });
    if (!this.ok(res)) {
      this.failure(res, "Could not update the signal.");
      return false;
    }
    // the response is the updated row: swap it in rather than wait on a second round trip
    const row = res.data && res.data.signal ? res.data : null;
    if (row && Array.isArray(this.saved)) this.saved = this.saved.map(r => (r && r.signal && r.signal.id === id ? row : r));
    else await this.fetchSaved();
    return true;
  }

  @action.bound
  async deleteSignal(id) {
    const res = await makeApi({ url: this.fill(URLS.signalsSavedOne, { id }), method: "DELETE" });
    if (!this.ok(res)) {
      this.failure(res, "Could not delete the signal.");
      return false;
    }
    await this.fetchSaved();
    return true;
  }

  // view: "open" | "done" | "all". page 0 replaces the list; later pages append.
  @action.bound
  // `reuseWithinMs`: a mount that may land just after the page's own early load finished takes that answer instead of asking again.
  async fetchFeed({ view = "open", savedSignalId = null, entityId = null, page = 0, size = 25, reuseWithinMs = 0 } = {}) {
    const key = JSON.stringify([view, savedSignalId || null, entityId || null, page, size]);
    if (this._feedInflight && this._feedInflight.key === key) return this._feedInflight.promise;
    const last = this._feedLast;
    if (reuseWithinMs && last && last.key === key && Date.now() - last.at < reuseWithinMs && !this.feedError) return this.feedItems;
    const promise = this._loadFeed({ view, savedSignalId, entityId, page, size });
    this._feedInflight = { key, promise };
    promise.then(() => {
      if (this._feedInflight && this._feedInflight.promise === promise) this._feedInflight = null;
      this._feedLast = { key, at: Date.now() };
    });
    return promise;
  }

  @action.bound
  async _loadFeed({ view, savedSignalId, entityId, page, size }) {
    if (entityId) return this._loadEntityFeed({ view, entityId, page, size });
    this.feedLoading = true;
    const params = { view, page, size };
    if (savedSignalId) params.savedSignalId = savedSignalId;
    if (entityId) params.entityId = entityId; // one account's or contact's signals (its page)
    const res = await makeApi({ url: URLS.signalsFeed, method: "GET", params });
    this.feedError = !(this.ok(res) && res.data);
    if (!this.feedError) {
      const items = res.data.items || [];
      this.feedItems = page === 0 ? items : this.feedItems.concat(items);
      this.feedTotal = res.data.total || 0;
      if (page === 0) {
        this.newSinceOpen = 0;
        this.feedLoadedAt = Date.now();
        this.latestItems = items;
      }
    }
    this.feedLoading = false;
    return this.feedItems;
  }

  /** What an embedded feed shows: its own slot when it has an entity, else the Signals feed. */
  feedFor(entityId) {
    if (!entityId) return { items: this.feedItems, total: this.feedTotal, loading: this.feedLoading, error: this.feedError, loadedAt: this.feedLoadedAt };
    return this.entityFeeds[entityId] || { items: [], total: 0, loading: true, error: false, loadedAt: 0 };
  }

  @action.bound
  _setEntityFeed(entityId, patch) {
    this.entityFeeds = { ...this.entityFeeds, [entityId]: { ...this.feedFor(entityId), ...patch } };
  }

  @action.bound
  async _loadEntityFeed({ view, entityId, page, size }) {
    this._setEntityFeed(entityId, { loading: true });
    const res = await makeApi({ url: URLS.signalsFeed, method: "GET", params: { view, page, size, entityId } });
    const ok = this.ok(res) && res.data;
    const prev = this.feedFor(entityId).items;
    const items = ok ? res.data.items || [] : [];
    this._setEntityFeed(entityId, ok
      ? { items: page === 0 ? items : prev.concat(items), total: res.data.total || 0, loading: false, error: false, loadedAt: Date.now() }
      : { loading: false, error: true });
    return this.feedFor(entityId).items;
  }

  // Applies one change to every loaded copy of the rows (the Signals feed and each account's or contact's own).
  @action.bound
  _mapRows(fn) {
    this.feedItems = this.feedItems.map(fn);
    const next = {};
    Object.keys(this.entityFeeds).forEach(id => { next[id] = { ...this.entityFeeds[id], items: this.entityFeeds[id].items.map(fn) }; });
    this.entityFeeds = next;
  }

  // Counts open entries newer than the newest one on screen, without touching the list itself.
  @action.bound
  async checkNewer({ savedSignalId = null, entityId = null } = {}) {
    // An account's or contact's own rows just reload quietly; the "new since you opened" pill is the Signals feed's.
    if (entityId) return this._loadEntityFeed({ view: "all", entityId, page: 0, size: 25 });
    const newest = Math.max(0, ...this.feedItems.map(i => new Date((i.event || {}).detectedAt).getTime() || 0));
    const params = { view: "open", page: 0, size: 25 };
    if (savedSignalId) params.savedSignalId = savedSignalId;
    if (entityId) params.entityId = entityId;
    const res = await makeApi({ url: URLS.signalsFeed, method: "GET", params });
    if (!(this.ok(res) && res.data)) {
      // The rows on screen stay; the feed shows a quiet "Couldn't refresh" line until a load succeeds.
      this.feedError = true;
      return this.newSinceOpen;
    }
    const items = res.data.items || [];
    this.feedError = false;
    this.latestItems = items;
    this.newSinceOpen = items.filter(i => new Date((i.event || {}).detectedAt).getTime() > newest).length;
    return this.newSinceOpen;
  }

  // Unread entries of one saved signal across the loaded feed and the last poll (the feed's own copy wins, it knows what was read).
  unreadFor(savedSignalId) {
    const byId = {};
    this.latestItems.concat(this.feedItems).forEach(i => {
      if (i.event && i.event.id) byId[i.event.id] = i;
    });
    return Object.values(byId).filter(i => i.event.savedSignalId === savedSignalId && !i.event.read && i.event.state === "OPEN").length;
  }

  // What tracking would cost: {records, alreadyWatched, newRecords, creditsPerCheck, checksPerMonth, creditsPerMonth},
  // plus `capped: true` when the server stopped counting early (the form then shows "N+").
  // Resolves to null when the server can't say (the form then falls back to the per-record price).
  @action.bound
  async estimate(payload) {
    const res = await makeApi({ url: URLS.signalsEstimate, method: "POST", data: payload });
    return this.ok(res) && res.data ? res.data : null;
  }

  @action.bound
  async fetchRecent(size = 5) {
    const res = await makeApi({ url: URLS.signalsFeed, method: "GET", params: { view: "open", page: 0, size } });
    if (this.ok(res) && res.data) this.recentItems = res.data.items;
    return this.recentItems;
  }

  @action.bound
  async fetchNotificationSettings() {
    const res = await makeApi({ url: URLS.signalsNotificationSettings, method: "GET" });
    if (this.ok(res) && res.data) this.notificationSettings = res.data;
    return this.notificationSettings;
  }

  // Resolves to {settings} or {error} so the panel can show the server's reason inline.
  @action.bound
  async saveNotificationSettings(payload) {
    const res = await makeApi({ url: URLS.signalsNotificationSettings, method: "PUT", data: payload });
    if (!this.ok(res)) {
      const error = (res && res.response && res.response.data && res.response.data.error) || "Could not save your notification settings.";
      return { error };
    }
    this.notificationSettings = res.data;
    return { settings: res.data };
  }

  @action.bound
  async fetchUnreadCount() {
    const res = await makeApi({ url: URLS.signalsUnreadCount, method: "GET" });
    if (this.ok(res) && res.data) this.unreadCount = res.data.count;
    return this.unreadCount;
  }

  // Keeps the sidebar badge fresh. Safe to call twice — only one timer runs.
  startPolling() {
    if (this._pollTimer) return;
    this.fetchUnreadCount();
    this._pollTimer = setInterval(() => this.fetchUnreadCount(), POLL_MS);
  }

  stopPolling() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = null;
  }

  // Updates one entry in place so the list doesn't jump, then refreshes the badge.
  @action.bound
  async act(eventId, kind, body) {
    const urls = { read: URLS.signalsFeedRead, dismiss: URLS.signalsFeedDismiss, handled: URLS.signalsFeedHandled, snooze: URLS.signalsFeedSnooze };
    const res = await makeApi({ url: this.fill(urls[kind], { id: eventId }), method: "POST", data: body });
    if (!this.ok(res)) {
      this.failure(res, "Could not update that signal.");
      return false;
    }
    if (kind === "read") this._mapRows(i => ((i.event || {}).id === eventId ? { ...i, event: { ...i.event, read: true } } : i));
    this.fetchUnreadCount();
    return true;
  }

  /**
   * Records what the member did from the feed (draft_email, add_to_sequence, add_to_list, find_email, call, open, dismiss)
   * and updates that row in place: it stays, greyed with what was done (dismissed too).
   * targetName is display-only ("You added them to Champions"). Falls back to the older dismiss/handled
   * endpoints while a backend without /action answers 404.
   */
  @action.bound
  async recordAction(eventId, kind, { targetId, targetName } = {}) {
    let res = await makeApi({ url: this.fill(URLS.signalsFeedAction, { id: eventId }), method: "POST", data: { action: kind, ...(targetId ? { targetId } : {}) } });
    if (res && res.response && res.response.status === 404) {
      res = await makeApi({ url: this.fill(kind === "dismiss" ? URLS.signalsFeedDismiss : URLS.signalsFeedHandled, { id: eventId }), method: "POST" });
    }
    if (!this.ok(res)) {
      this.failure(res, "Could not update that signal.");
      return false;
    }
    this.markActed(eventId, kind, targetName, targetId);
    this.fetchUnreadCount();
    this.fetchSuggestions();
    return true;
  }

  /**
   * An entry's suggested email: GET generates one on demand, regenerate rewrites it ({instruction}). Both answer
   * {draftStatus, draft} (or the draft itself) and update the row in place. Resolves {draftStatus, draft} or null.
   * `quiet`: a background re-check that says nothing when it fails.
   */
  @action.bound
  async fetchDraft(eventId, { quiet = false } = {}) {
    const res = await makeApi({ url: this.fill(URLS.signalsFeedDraft, { id: eventId }), method: "GET" });
    return this.takeDraft(eventId, res, quiet ? null : "Couldn't write a draft right now. Try again.");
  }

  @action.bound
  async regenerateDraft(eventId, instruction) {
    const res = await makeApi({ url: this.fill(URLS.signalsFeedDraftRegenerate, { id: eventId }), method: "POST", data: instruction ? { instruction } : {} });
    return this.takeDraft(eventId, res, "Couldn't rewrite the draft. Try again.");
  }

  @action.bound
  takeDraft(eventId, res, fallback) {
    if (!(this.ok(res) && res.data)) {
      if (fallback) this.failure(res, fallback);
      return null;
    }
    const body = res.data;
    const draft = body.draft !== undefined ? body.draft : body.subject || body.body ? body : null;
    const draftStatus = body.draftStatus || (draft ? "ready" : "pending");
    this._mapRows(i => ((i.event || {}).id === eventId ? { ...i, draft: draft || i.draft || null, draftStatus } : i));
    return { draftStatus, draft };
  }

  @action.bound
  markActed(eventId, kind, targetName, targetId) {
    const isIt = i => (i.event || {}).id === eventId;
    this._mapRows(i =>
      isIt(i) ? { ...i, actionType: kind, actionTargetName: targetName || null, actionTargetId: targetId || null, event: { ...i.event, read: true, state: kind === "dismiss" ? "DISMISSED" : "HANDLED" } } : i
    );
  }

  // The "let an agent do it" nudges. Quietly empty when the server can't say.
  @action.bound
  async fetchSuggestions() {
    const res = await makeApi({ url: URLS.signalsSuggestions, method: "GET" });
    this.suggestions = this.ok(res) && Array.isArray(res.data) ? res.data : [];
    return this.suggestions;
  }

  // Hidden for 30 days on the server; gone from the page right away either way.
  @action.bound
  async dismissSuggestion(signalType) {
    this.suggestions = this.suggestions.filter(s => s.signalType !== signalType);
    await makeApi({ url: this.fill(URLS.signalsSuggestionDismiss, { type: signalType }), method: "POST" });
  }

  // Loaded once a session (one request even when several pickers mount at once); a failed load asks again next time.
  @action.bound
  fetchSellerProfiles() {
    if (this.sellerProfiles) return Promise.resolve(this.sellerProfiles);
    if (this._sellerProfilesInflight) return this._sellerProfilesInflight;
    this._sellerProfilesInflight = makeApi({ url: URLS.signalsSellerProfiles, method: "GET" }).then(res => {
      this._sellerProfilesInflight = null;
      if (this.ok(res) && res.data) this.setSellerProfiles(res.data);
      return this.sellerProfiles;
    });
    return this._sellerProfilesInflight;
  }

  @action.bound
  setSellerProfiles(d) {
    this.sellerProfiles = { defaultId: d.defaultId || null, profiles: Array.isArray(d.profiles) ? d.profiles : [] };
  }

  // A failed load leaves `competitors` as it was (null on first load) so the page can offer a retry.
  // `sellerProfileId` asks for one profile's competitors; without it the server uses the member's choice.
  @action.bound
  async fetchCompetitors({ sellerProfileId } = {}) {
    this.competitorsLoading = true;
    const res = await makeApi({ url: URLS.signalsCompetitors, method: "GET", ...(sellerProfileId ? { params: { sellerProfileId } } : {}) });
    if (this.ok(res) && res.data) this.applyCompetitors(res.data, sellerProfileId);
    this.competitorsLoading = false;
    return this.competitors;
  }

  // Takes a competitors view (GET, or the answer to an add/fix/remove) into the store and the per-profile cache.
  @action.bound
  applyCompetitors(d, sellerProfileId) {
    const next = {
      hasSellerProfile: !!d.hasSellerProfile,
      sellerProfileId: d.sellerProfileId || null,
      competitors: d.competitors || [],
      profiles: d.profiles || (this.competitors && this.competitors.profiles) || [],
      profileSource: d.profileSource || null,
      reason: d.reason || null
    };
    if (next.sellerProfileId && !next.reason) this.competitorsByProfile[next.sellerProfileId] = next;
    // A late answer for a profile the member has already switched away from only fills the cache.
    if (!sellerProfileId || !this.competitors || this.competitors.sellerProfileId === sellerProfileId) this.competitors = next;
  }

  // The server's own reason for a 4xx ("Already in your competitors"); a 5xx's body is a stack-level message, never shown.
  reasonOf(res) {
    const r = res && res.response;
    return r && r.status < 500 && r.data && r.data.error ? r.data.error : "";
  }

  /** Companies matching a name, website or LinkedIn company URL: {candidates} or {error}. */
  @action.bound
  async lookupCompetitors(query) {
    const res = await makeApi({ url: URLS.signalsCompetitorsLookup, method: "POST", data: { query } });
    if (!this.ok(res)) return { error: this.reasonOf(res) || "Couldn't look that up right now. Try again." };
    return { candidates: (res.data && res.data.candidates) || [] };
  }

  // add / fix / remove / restore all answer the full list; {ok} or {error} with the server's reason (422 "Already in your competitors").
  async editCompetitors(req, fallback) {
    const sellerProfileId = this.competitors && this.competitors.sellerProfileId;
    const withProfile = sellerProfileId ? { sellerProfileId } : {};
    const res = await makeApi(req.method === "DELETE" ? { ...req, params: { ...withProfile, ...req.params } } : { ...req, data: { ...withProfile, ...req.data } });
    if (!this.ok(res)) return { error: this.reasonOf(res) || fallback };
    if (res.data && res.data.competitors) this.applyCompetitors(res.data, sellerProfileId);
    else await this.fetchCompetitors(sellerProfileId ? { sellerProfileId } : {});
    return { ok: true };
  }

  /** Adds a picked company and starts reading its posts. */
  @action.bound
  async addCompetitor({ name, domain, linkedinUrl }) {
    const out = await this.editCompetitors({ url: URLS.signalsCompetitors, method: "POST", data: { name, domain, linkedinUrl } }, "Couldn't add that competitor. Try again.");
    if (out.ok && domain) this.fetchCompetitorInsights(domain);
    return out;
  }

  /** Points an existing row (by its name) at the company the member picked, and reads that company's posts. */
  @action.bound
  async matchCompetitor(name, { domain, linkedinUrl }) {
    const out = await this.editCompetitors({ url: URLS.signalsCompetitorsMatch, method: "PUT", data: { name, domain, linkedinUrl } }, "Couldn't change that company. Try again.");
    if (out.ok && domain) this.fetchCompetitorInsights(domain, { force: true });
    return out;
  }

  @action.bound
  async removeCompetitor(name) {
    return this.editCompetitors({ url: URLS.signalsCompetitors, method: "DELETE", params: { name } }, "Couldn't remove that competitor. Try again.");
  }

  @action.bound
  async restoreCompetitor(name) {
    return this.editCompetitors({ url: URLS.signalsCompetitorsRestore, method: "POST", data: { name } }, "Couldn't bring that competitor back. Try again.");
  }

  // Saves which seller profile is "my company" for this member, then loads its competitors. Insights stay cached by domain.
  @action.bound
  async chooseCompetitorProfile(sellerProfileId) {
    const prev = this.competitors;
    // Header and picker move to the chosen profile now: its cached list if we have one, else an empty list while it loads.
    const cached = this.competitorsByProfile[sellerProfileId];
    const optimistic = cached
      ? { ...cached, profiles: (prev && prev.profiles) || cached.profiles }
      : { ...(prev || {}), sellerProfileId, competitors: [], profileSource: null, reason: null, hasSellerProfile: true };
    this.competitors = optimistic;
    // The PUT only remembers the choice; the list is asked for by id, so both go at once.
    const [res] = await Promise.all([
      makeApi({ url: URLS.signalsCompetitorsProfile, method: "PUT", data: { sellerProfileId } }),
      this.fetchCompetitors({ sellerProfileId })
    ]);
    if (!this.ok(res)) {
      if (this.competitors && this.competitors.sellerProfileId === sellerProfileId) this.competitors = prev;
      return false;
    }
    // The list itself failed to load and nothing was cached: offer a retry rather than an empty profile.
    // (A good answer always lands in competitorsByProfile or carries its own reason.)
    const now = this.competitors;
    if (!cached && !this.competitorsByProfile[sellerProfileId] && now && now.sellerProfileId === sellerProfileId && !now.reason) {
      this.competitors = { ...optimistic, reason: "unavailable" };
    }
    return true;
  }

  @action.bound
  setCompetitorInsights(domain, entry) {
    this.competitorInsights = { ...this.competitorInsights, [domain]: entry };
  }

  /**
   * One competitor's recent posts and what they mean. Cached per domain for the session (the server caches a day);
   * `force` re-asks. A failed call answers {reason: "unavailable"} and is not cached, so a retry asks again.
   */
  @action.bound
  async fetchCompetitorInsights(domain, { force = false } = {}) {
    if (!domain) return { posts: [], insights: [], reason: "no_linkedin_page" };
    const have = this.competitorInsights[domain];
    if (have && !force && !have.failed) return have.promise || have.data;
    const promise = makeApi({ url: URLS.signalsCompetitorInsights, method: "GET", params: { domain } }).then(res => {
      const good = this.ok(res) && res.data;
      const data = good ? { posts: res.data.posts || [], peoplePosts: res.data.peoplePosts || [], insights: res.data.insights || [], generatedAt: res.data.generatedAt || null, reason: res.data.reason || null } : { posts: [], peoplePosts: [], insights: [], generatedAt: null, reason: "unavailable" };
      this.setCompetitorInsights(domain, { loading: false, data, failed: !good || data.reason === "unavailable" });
      return data;
    });
    this.setCompetitorInsights(domain, { loading: true, data: null, promise });
    return promise;
  }

  @action.bound
  async markAllRead() {
    const res = await makeApi({ url: URLS.signalsReadAll, method: "POST" });
    if (this.ok(res)) {
      this.feedItems = this.feedItems.map(i => ({ ...i, event: { ...i.event, read: true } }));
      this.unreadCount = 0;
    }
    return this.ok(res);
  }
}

export const signalsData = new SignalsData();
